import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router';
import { FullPageSpinner } from '@ui/Spinner';
import { RequireAuth } from '@core/auth/RequireAuth';
import { RequirePermission } from '@core/auth/RequirePermission';
import { isSsoOnly } from '@core/config/features';
import { AppLayout } from '@core/layout/AppLayout';
import { LoginPage } from '@features/auth/LoginPage';

/**
 * ROUTE-LEVEL CODE SPLITTING.
 *
 * Angular lazy-loaded EVERY route with `loadComponent: () => import(...)`. These imports were eager,
 * so a single 980 kB chunk held every screen: signing in downloaded the 3,000-line vendor-view form,
 * every dialog and the whole settings area before the dashboard could paint.
 *
 * `LoginPage` stays EAGER on purpose — it is the entry screen, and a separate chunk for it would add
 * a round trip before first paint. The Suspense boundary lives around the Outlet in `AppLayout`, so
 * the shell stays mounted while a page chunk downloads.
 */
const CreateItemsPage = lazy(() =>
  import('@features/create-items/CreateItemsPage').then((m) => ({ default: m.CreateItemsPage })),
);
const DashboardPage = lazy(() =>
  import('@features/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const BusinessPartnersPage = lazy(() =>
  import('@features/dashboard/BusinessPartnersPage').then((m) => ({
    default: m.BusinessPartnersPage,
  })),
);
const RequestsPage = lazy(() =>
  import('@features/requests/RequestsPage').then((m) => ({ default: m.RequestsPage })),
);
const ExportDownloadPage = lazy(() =>
  import('@features/exports/ExportDownloadPage').then((m) => ({
    default: m.ExportDownloadPage,
  })),
);
const RequestHistoryPage = lazy(() =>
  import('@features/requests/RequestHistoryPage').then((m) => ({
    default: m.RequestHistoryPage,
  })),
);
const VendorViewPage = lazy(() =>
  import('@features/vendor-view/VendorViewPage').then((m) => ({ default: m.VendorViewPage })),
);
const VendorExtensionHistoryPage = lazy(() =>
  import('@features/vendor-view/VendorExtensionHistoryPage').then((m) => ({
    default: m.VendorExtensionHistoryPage,
  })),
);
const VendorExtensionHistoryDetailPage = lazy(() =>
  import('@features/vendor-view/VendorExtensionHistoryDetailPage').then((m) => ({
    default: m.VendorExtensionHistoryDetailPage,
  })),
);
const VendorExtensionPage = lazy(() =>
  import('@features/dashboard/dialogs/VendorExtensionDialog').then((m) => ({
    default: m.VendorExtensionPage,
  })),
);
const TierChangePage = lazy(() =>
  import('@features/dashboard/dialogs/TierChangeDialog').then((m) => ({
    default: m.TierChangePage,
  })),
);
const VendorDetailsPage = lazy(() =>
  import('@features/vendor-details/VendorDetailsPage').then((m) => ({
    default: m.VendorDetailsPage,
  })),
);
const DuplicateComparePage = lazy(() =>
  import('@features/vendor/DuplicateComparePage').then((m) => ({
    default: m.DuplicateComparePage,
  })),
);
const ForgotPasswordPage = lazy(() =>
  import('@features/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
);
const NotFoundPage = lazy(() =>
  import('@features/not-found/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
);
const LegacySettingsTabRedirect = lazy(() =>
  import('@features/settings/LegacySettingsTabRedirect').then((m) => ({
    default: m.LegacySettingsTabRedirect,
  })),
);
const SettingsLayout = lazy(() =>
  import('@features/settings/SettingsLayout').then((m) => ({ default: m.SettingsLayout })),
);
const ManageUsersPage = lazy(() =>
  import('@features/settings/users/ManageUsersPage').then((m) => ({ default: m.ManageUsersPage })),
);
const AssignActivitiesPage = lazy(() =>
  import('@features/settings/roles/AssignActivitiesPage').then((m) => ({
    default: m.AssignActivitiesPage,
  })),
);
const ManageRolesPage = lazy(() =>
  import('@features/settings/roles/ManageRolesPage').then((m) => ({ default: m.ManageRolesPage })),
);
const RoleUsersPage = lazy(() =>
  import('@features/settings/roles/RoleUsersPage').then((m) => ({ default: m.RoleUsersPage })),
);
const CreateUserPage = lazy(() =>
  import('@features/settings/users/CreateUserPage').then((m) => ({ default: m.CreateUserPage })),
);
const EditUserPage = lazy(() =>
  import('@features/settings/users/EditUserPage').then((m) => ({ default: m.EditUserPage })),
);
const BulkImportPage = lazy(() =>
  import('@features/settings/data/BulkCustomerUploadPage').then((m) => ({
    default: m.BulkCustomerUploadPage,
  })),
);
const DelegationPage = lazy(() =>
  import('@features/delegation/DelegationPage').then((m) => ({ default: m.DelegationPage })),
);
const DelegationApprovalsPage = lazy(() =>
  import('@features/delegation/DelegationApprovalsPage').then((m) => ({
    default: m.DelegationApprovalsPage,
  })),
);
const MyProfilePage = lazy(() =>
  import('@features/settings/profile/MyProfilePage').then((m) => ({ default: m.MyProfilePage })),
);
const AiSearchPage = lazy(() =>
  import('@features/ai-search/AiSearchPage').then((m) => ({ default: m.AiSearchPage })),
);
const ApprovalWorkflowsPage = lazy(() =>
  import('@features/workflow/ApprovalWorkflowsPage').then((m) => ({
    default: m.ApprovalWorkflowsPage,
  })),
);
const CompanyMatrixPage = lazy(() =>
  import('@features/workflow/CompanyMatrixPage').then((m) => ({ default: m.CompanyMatrixPage })),
);

/**
 * Route table. Paths mirror `src/app/app.routes.ts` exactly, with three deliberate
 * differences:
 *
 * 1. `/settings` sub-views are REAL NESTED ROUTES. The Angular version switched an enum
 *    behind a single `?tab=` param that only covered three of its five sub-views, so
 *    Create User and Assign Activities were lost on refresh and the Back button did not
 *    work for them. `LegacySettingsTabRedirect` keeps `?tab=` links working.
 *
 * 2. `/ai-search` IS GUARDED. In Angular it was the only child route with no
 *    `canActivate`, so an unauthenticated user could reach it by typing the URL.
 *
 * 3. A `*` route exists. Angular's wildcard is commented out, so an unknown URL renders
 *    a blank screen.
 *
 * DROPPED: `/SettngcComponent`. It routed to the column-preferences modal as a standalone
 * page; nothing links to it and the component is only meaningful inside the dashboard.
 *
 * Routes are added phase by phase as their pages land, so every registered path resolves
 * to a working screen rather than a placeholder.
 */
const routes: RouteObject[] = [
  { index: true, element: <Navigate to="/login" replace /> },

  { path: 'login', element: <LoginPage /> },
  {
    path: 'forgot-password',
    // Its own boundary: this is the one lazy route outside `AppLayout`, so it cannot rely on the
    // Suspense that wraps the Outlet there.
    // SSO only (`VITE_SSO_ONLY`): there is no password to reset, so a bookmarked link goes to /login.
    element: isSsoOnly() ? (
      <Navigate to="/login" replace />
    ) : (
      <Suspense fallback={<FullPageSpinner message="Loading…" />}>
        <ForgotPasswordPage />
      </Suspense>
    ),
  },

  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: 'dashboard', element: <DashboardPage /> },

          // The master BP form. `?mode=` selects create | edit | view; `?draft=` carries the
          // keyed draft. Both the id-less and id-bearing forms exist, as in app.routes.ts.
          { path: 'vendor-view', element: <VendorViewPage /> },
          {
            path: 'vendor-view/:id',
            children: [
              { index: true, element: <VendorViewPage /> },
              { path: 'extension', element: <VendorExtensionPage /> },
              { path: 'tier-change', element: <TierChangePage /> },
              { path: 're-rating', element: <TierChangePage isReRating /> },
              { path: 'extension-history', element: <VendorExtensionHistoryPage /> },
              {
                path: 'extension-history/:extensionRequestId',
                element: <VendorExtensionHistoryDetailPage />,
              },
            ],
          },
          { path: 'business-partners', element: <BusinessPartnersPage /> },
          /* Partners returned for review — its own sidebar entry, formerly the last tab above. */
          { path: 'review', element: <BusinessPartnersPage only="reviewed" /> },

          /**
           * The approver's queue. NEW — ported from the CMD prototype, no Angular equivalent exists.
           *
           * Gated on `vendor:approve`, the action its three verdict buttons post with and the same
           * permission `getRowActions` requires before it renders Approve/Reject/Review on a row. A
           * user without it would reach a queue whose every control is inert.
           *
           * ⚠️ `orDelegatee` ADMITS A DELEGATE TOO. Somebody standing in for an approver may hold no
           * `vendor:approve` of their own, and this queue is precisely where the delegation is meant to
           * be exercised — without it they would be bounced to the dashboard by the screen the
           * delegation exists to give them. The row actions widen the same way; see
           * `canApproveWorkflows`.
           *
           * The prototype marks this screen Supervisor-only in its role switcher, which is what that
           * permission expresses in the real model.
           */
          {
            path: 'requests',
            element: (
              <RequirePermission action="vendor:approve" orDelegatee>
                <RequestsPage />
              </RequirePermission>
            ),
          },
          {
            path: 'requests/tracking',
            // Read-only and company-scoped by the API. Any authenticated user
            // may open it; the backend decides which companies/sales orgs they see.
            element: <RequestHistoryPage />,
          },
          { path: 'requests/history', element: <Navigate to="/requests/tracking" replace /> },

          /**
           * The emailed link to a Business Partner export built in the background. Gated on
           * `vendor:export`, the permission every export endpoint requires — see `ExportDialog`.
           */
          {
            path: 'exports/:jobId',
            element: (
              <RequirePermission action="vendor:export">
                <ExportDownloadPage />
              </RequirePermission>
            ),
          },

          /**
           * ============================================================================
           * Delegation — TOP LEVEL, not under /settings.
           * ============================================================================
           *
           * Delegating your own approval authority is something an approver does in the course of their
           * work, not an administrative setting. It sits beside Requests for that reason: both are about
           * approvals passing through you.
           *
           * ⚠️ THE REQUESTER'S SCREEN IS UNGATED ON PURPOSE. Raising a delegation is asking for something,
           * not granting it — the request is inert until an admin approves. Gating it on a user-admin
           * action would stop ordinary approvers, the only people who need it, from reaching it.
           */
          { path: 'delegation', element: <DelegationPage /> },

          /**
           * The admin queue. `del_request:view` to reach it; the page itself checks
           * `del_request:approve` before offering a verdict and shows an access panel without it, so a
           * user entitled to see the queue can do so without being able to decide it.
           *
           * These replaced a `usermanage:*` stand-in once the API began issuing delegation permissions
           * of its own. ⚠️ Neither is `is_delegatee`: these say "you administer other people's
           * delegation requests", the flag says "somebody delegated their authority to you".
           */
          {
            path: 'delegation-approvals',
            element: (
              <RequirePermission action="del_request:view">
                <DelegationApprovalsPage />
              </RequirePermission>
            ),
          },

          /**
           * AI Search.
           *
           * Two gaps closed here. In `app.routes.ts` this was the only child route with NO
           * `canActivate` at all, so typing the URL reached it unauthenticated — `RequireAuth` fixes
           * that.
           *
           * And it now needs `ai:search`. An earlier note here claimed "the live permission list has no
           * AI-search action" — that was WRONG: `ai:search` exists, and the sidebar has always gated
           * this link on it. Testing with a `role:view`-only account made the contradiction visible —
           * the link was correctly hidden and the page loaded anyway by URL.
           */
          {
            path: 'ai-search',
            element: (
              <RequirePermission action="ai:search">
                <AiSearchPage />
              </RequirePermission>
            ),
          },

          /**
           * Approval workflow definitions. VIEW-ONLY, because that is what the Angular screen is —
           * both buttons that opened its creation drawer are commented out of the template, so the
           * drawer is unreachable. See `ApprovalWorkflowsPage` for the full account.
           *
           * No permission gate: `app.routes.ts` guarded it with `authGuard` only, and the live
           * permission list has no workflow action.
           */
          {
            path: 'approval-workflow',
            /**
             * Gated on the `vendor_approvals` GROUP, which is what the sidebar link already uses —
             * `main-layout.component.html` line 70 wrapped it in
             * `isModuleVisible('vendor_approvals')`.
             *
             * Angular's route itself carried only `authGuard`, so a user whose sidebar correctly hid
             * this link could still reach it by typing the URL. Found by testing with a restricted
             * account (`commercial@vendor.com`, which lacks the group): the link was hidden and the
             * page loaded anyway. Same class of gap as `/ai-search`, closed the same way.
             */
            element: (
              <RequirePermission module="vendor_approvals">
                <ApprovalWorkflowsPage />
              </RequirePermission>
            ),
          },

          /**
           * DOA by company code. NEW SCREEN, read-only, from `/approval-matrix/company-code/{code}`.
           *
           * SEPARATE FROM `/approval-workflow` on purpose. That route shows the workflow DEFINITIONS —
           * global, one chain per module. This shows how those chains route PER COMPANY CODE, where the
           * same module can differ company by company. Different endpoints, different questions.
           *
           * ⚠️ GATED ON `matrix:view`, NOT on `vendor_approvals`. It borrowed the neighbour's gate only
           * because no matrix permission existed at the time; the backend now issues an
           * `approval_matrix` group with `matrix:view` / `matrix:update`, so the screen uses the
           * permission that was minted for it. `vendor_approvals` would have let anyone who can approve
           * a workflow read every company's routing.
           */
          {
            path: 'doa-matrix',
            element: (
              <RequirePermission action="matrix:view">
                <CompanyMatrixPage />
              </RequirePermission>
            ),
          },

          /**
           * `/profile` renders My Profile DIRECTLY. Angular had a `profile.component` wrapper (127
           * LOC) that rendered this and nothing else; it is not ported.
           *
           * No permission guard: every authenticated user has a profile.
           */
          { path: 'profile', element: <MyProfilePage /> },

          {
            path: 'create-items',
            element: (
              <RequirePermission action="vendor:create">
                <CreateItemsPage />
              </RequirePermission>
            ),
          },

          // The create flow: vendor-details (identity + duplicate check) → vendor (comparison)
          // → vendor-view (the master form). Only a `?draft=` id crosses each hop.
          {
            path: 'vendor-details',
            element: (
              <RequirePermission action="vendor:create">
                <VendorDetailsPage />
              </RequirePermission>
            ),
          },
          {
            path: 'vendor-details/:id',
            element: (
              <RequirePermission action="vendor:update">
                <VendorDetailsPage />
              </RequirePermission>
            ),
          },
          { path: 'vendor', element: <DuplicateComparePage /> },

          /**
           * Settings — REAL nested routes, replacing the switched enum.
           *
           * `settings.component.ts` held a `currentView` enum and read `?tab=` for only three of its
           * five sub-views, so Create User and Assign Activities had no URL at all: a refresh lost
           * whatever was in progress and neither could be linked to. Each is a route now.
           *
           * `/settings` stays an INDEX route so `?tab=manageUsers` bookmarks keep working. A sibling
           * `path: 'settings'` entry would shadow these children.
           *
           * Only the routes whose screens exist are registered. The remaining four are marked
           * `pending` in `SETTINGS_TABS`, which renders them inert in the rail rather than as links
           * to a 404. Add the route here as each screen lands and drop its flag.
           */
          {
            path: 'settings',
            element: <SettingsLayout />,
            children: [
              { index: true, element: <LegacySettingsTabRedirect /> },
              {
                path: 'users',
                element: (
                  <RequirePermission action="usermanage:view">
                    <ManageUsersPage />
                  </RequirePermission>
                ),
              },
              {
                path: 'users/new',
                element: (
                  <RequirePermission action="usermanage:create">
                    <CreateUserPage />
                  </RequirePermission>
                ),
              },
              {
                path: 'users/:userId/edit',
                element: (
                  <RequirePermission action="usermanage:update">
                    <EditUserPage />
                  </RequirePermission>
                ),
              },
              {
                path: 'roles',
                element: (
                  <RequirePermission action="role:view">
                    <ManageRolesPage />
                  </RequirePermission>
                ),
              },
              /**
               * Nested under the role it edits, so a refresh keeps the in-progress permission
               * selection. In Angular this was an enum value with no URL at all.
               *
               * Guarded on `role:update`, not `role:view`: this screen WRITES permissions.
               */
              /**
               * CREATE mode of the activities screen. Must precede `roles/:id/activities` for clarity,
               * though React Router already ranks a static segment above a dynamic one.
               *
               * A role and its actions are created in ONE call — `POST /roles/management/create`
               * rejects an empty `action_ids` — so the name/description form on Manage Roles hands off
               * here and the create happens on save. Gated on `role:create`, the permission that form's
               * button is gated on.
               */
              {
                path: 'roles/new/activities',
                element: (
                  <RequirePermission action="role:create">
                    <AssignActivitiesPage />
                  </RequirePermission>
                ),
              },
              {
                path: 'roles/:id/activities',
                element: (
                  <RequirePermission action="role:update">
                    <AssignActivitiesPage />
                  </RequirePermission>
                ),
              },
              /**
               * The users holding a role. In Angular this was the `showUserTable` boolean on Manage
               * Roles — no URL, so it could not be linked to and a refresh lost it.
               *
               * Guarded on `role:roleuser`, the permission Angular's `onViewUsers` itself checked.
               */
              {
                path: 'roles/:id/users',
                element: (
                  <RequirePermission action="role:roleuser">
                    <RoleUsersPage />
                  </RequirePermission>
                ),
              },
              /**
               * `bulk:upload`, NOT `usermanage:create`.
               *
               * This gate was originally `usermanage:create` on the reasoning that bulk import creates
               * users. That was wrong, and testing with a restricted account proved it:
               * `commercial@vendor.com` is granted `bulk:upload` and shown a visible `bulk_upload`
               * group — it is the permission the backend issues precisely so a user can bulk-upload —
               * yet it lacks `usermanage:create`, so the screen its permission was granted for was
               * unreachable.
               *
               * Angular gated this screen on nothing at all (it was an enum value behind `?tab=`), so
               * there is no prior art to copy; `bulk:upload` is what the live API models.
               */
              {
                path: 'data',
                element: (
                  <RequirePermission action="bulk:upload">
                    <BulkImportPage />
                  </RequirePermission>
                ),
              },
            ],
          },

          { index: true, element: <Navigate to="/dashboard" replace /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
